import React, { useCallback, useMemo } from 'react'
import { Grid, Row, Col, Checkbox } from 'react-bootstrap'
import { Button } from 'syft-acp-atoms/Button'
import { useFlexFlagIsOn } from '@indeed/flex-feature-flags'

import DataDropdown from './DataDropdown'
import { filterSelectedFromOptions, getAgencyColumnWidths } from './dataAgenciesDropdownHelpers'

import { DataAgenciesDropdownItemProps } from './DataAgenciesDropdown.types'

const DataAgenciesDropdownItem = ({
  agency,
  options,
  values,
  header,
  deletable,
  editable,
  disabled,
  onChange,
  onDeleteClick,
  onChangeAllowCancellations,
  draggable = false,
  position = 0,
  total = 0,
  section = 'other',
  onDragStart,
  onDragEnd,
  onDragOverItem,
  onDropItem,
  onMoveUp,
  onMoveDown,
  onToggleSection,
  isDragging = false,
  isDragTarget = false,
}: DataAgenciesDropdownItemProps) => {
  const isAllowed24HoursDeletion = useFlexFlagIsOn('pte_41607_last_minute_booking_cancels')
  const filteredOptions = useMemo(
    () =>
      filterSelectedFromOptions({
        options: options,
        values,
        selected: agency.agency_id,
      }),
    [options, values, agency],
  )
  const handleChangeDropdown = useCallback(
    value => onChange(value, agency.rank, agency.id),
    [agency.rank, agency.id, onChange],
  )
  const handleChangeAllowCancellations = useCallback(
    () => onChangeAllowCancellations(agency.agency_id),
    [agency.agency_id, onChangeAllowCancellations],
  )
  const handleDelete = useCallback(() => onDeleteClick(agency.rank), [agency.rank, onDeleteClick])

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      // dataTransfer is absent in jsdom and may be locked down in some browsers; guard it. The
      // module-scoped drag holder in the parent is the source of truth, so this is best-effort only.
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        try {
          event.dataTransfer.setData('text/plain', String(agency.rank))
        } catch {
          // Required for Firefox; safe to ignore when the platform rejects setData.
        }
      }
      onDragStart?.(agency.rank)
    },
    [agency.rank, onDragStart],
  )
  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      onDragOverItem?.(agency.rank)
    },
    [agency.rank, onDragOverItem],
  )
  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      onDropItem?.(agency.rank)
    },
    [agency.rank, onDropItem],
  )
  const handleMoveUp = useCallback(() => onMoveUp?.(agency.rank), [agency.rank, onMoveUp])
  const handleMoveDown = useCallback(() => onMoveDown?.(agency.rank), [agency.rank, onMoveDown])
  const handleToggleSection = useCallback(
    () => onToggleSection?.(agency.rank),
    [agency.rank, onToggleSection],
  )
  // Short button text keeps the row on one line; the full intent lives in aria-label/title.
  const toggleSectionLabel = section === 'primary' ? '→ Other' : '→ Primary'
  const toggleSectionDescription = section === 'primary' ? 'Move to other agencies' : 'Make primary'

  const agencyLabel = agency.agency_name || `rank ${agency.rank}`

  // Shared 12-column contract (see getAgencyColumnWidths) keeps the row aligned with the header.
  // When reordering is on, the action cluster (arrows + section transfer + delete) takes a wide
  // column, so the agency dropdown is roughly halved from its original width.
  const columnWidths = getAgencyColumnWidths({
    canReorder: draggable,
    deletable,
    showCancelColumn: isAllowed24HoursDeletion,
  })
  const dropdownWidth = columnWidths.agencies
  const actionsWidth = columnWidths.actions

  const rowClassNames = [
    'dropdown-row',
    draggable ? 'DataAgenciesDropdown__row--draggable' : '',
    isDragging ? 'DataAgenciesDropdown__row--dragging' : '',
    isDragTarget ? 'DataAgenciesDropdown__row--drag-target' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const grid = (
    <Grid>
      <Row className={rowClassNames}>
        <Col md={1} className="DataAgenciesDropdown__rank">
          {draggable && (
            <span
              className="DataAgenciesDropdown__handle"
              draggable
              onDragStart={handleDragStart}
              onDragEnd={onDragEnd}
              aria-hidden="true"
              title="Drag to reorder"
            >
              ⠿
            </span>
          )}
          # {agency.rank}
        </Col>
        <Col md={dropdownWidth}>
          <DataDropdown
            onChange={handleChangeDropdown}
            options={filteredOptions}
            header={header}
            value={agency.agency_id}
            editable={editable}
            disabled={disabled}
          />
        </Col>
        {isAllowed24HoursDeletion && (
          <Col md={1}>
            <Checkbox
              style={{ placeSelf: 'center', marginTop: '5px' }}
              checked={agency?.allow_short_notice_booking_cancellations}
              onChange={handleChangeAllowCancellations}
              disabled={!agency.agency_id}
            />
          </Col>
        )}
        {/* Reorder, section transfer and delete sit together as one action cluster. */}
        {(draggable || deletable) && (
          <Col md={actionsWidth} className="DataAgenciesDropdown__reorder">
            {draggable && (
              <>
                <Button
                  kind="regular"
                  className="DataAgenciesDropdown__arrow"
                  onClick={handleMoveUp}
                  disabled={position <= 0}
                  aria-label={`Move ${agencyLabel} up`}
                  title="Move up"
                >
                  ↑
                </Button>{' '}
                <Button
                  kind="regular"
                  className="DataAgenciesDropdown__arrow"
                  onClick={handleMoveDown}
                  disabled={position >= total - 1}
                  aria-label={`Move ${agencyLabel} down`}
                  title="Move down"
                >
                  ↓
                </Button>{' '}
                <Button
                  kind="regular"
                  onClick={handleToggleSection}
                  aria-label={`${toggleSectionDescription}: ${agencyLabel}`}
                  title={toggleSectionDescription}
                >
                  {toggleSectionLabel}
                </Button>{' '}
              </>
            )}
            {deletable && (
              <Button
                kind="danger"
                onClick={handleDelete}
                disabled={!agency.agency_id && !values?.length}
                aria-label={`Delete rank: ${agencyLabel}`}
              >
                Delete
              </Button>
            )}
          </Col>
        )}
      </Row>
    </Grid>
  )

  // react-bootstrap's Grid typings don't expose DOM drag handlers, so when reordering is enabled we
  // wrap it in a div that owns the drop target. When disabled we render the Grid unchanged.
  if (!draggable) {
    return grid
  }

  return (
    <div onDragOver={handleDragOver} onDrop={handleDrop}>
      {grid}
    </div>
  )
}

export default DataAgenciesDropdownItem
